import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  TextField,
  MenuItem,
  Button,
  IconButton,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import useThemeStyles from '../../../../../hooks/useThemeStyles';
import { NOTE_CATEGORIES } from './NoteQuickAddForm';

/**
 * Dumb note row: shows the note, expands into an inline editor.
 * All persistence goes through the onSave / onDelete callbacks.
 */
const NoteEditItem = ({ note, onSave, onDelete }) => {
  const { colors } = useThemeStyles();
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(note.title || '');
  const [content, setContent] = useState(note.content || '');
  const [category, setCategory] = useState(note.category || 'General');
  const [busy, setBusy] = useState(false);

  const noteId = note._id || note.id;

  const textFieldSx = {
    '& .MuiInputLabel-root': { color: colors.text.secondary },
    '& .MuiInputLabel-root.Mui-focused': { color: colors.interactive.primary },
    input: { color: colors.text.primary },
    textarea: { color: colors.text.primary },
    '& .MuiSelect-select': { color: colors.text.primary },
    '& .MuiOutlinedInput-root': {
      '& fieldset': { borderColor: colors.border.subtle },
      '&:hover fieldset': { borderColor: colors.interactive.primary },
      '&.Mui-focused fieldset': { borderColor: colors.interactive.primary },
    },
  };

  const startEditing = () => {
    setTitle(note.title || '');
    setContent(note.content || '');
    setCategory(note.category || 'General');
    setEditing(true);
  };

  const handleSave = async () => {
    if (!title.trim() || busy) return;
    setBusy(true);
    const ok = await onSave(noteId, { title: title.trim(), content, category });
    setBusy(false);
    if (ok) {
      setEditing(false);
    }
  };

  const handleDelete = async () => {
    if (busy) return;
    setBusy(true);
    await onDelete(noteId);
    setBusy(false);
  };

  return (
    <Card
      variant="outlined"
      sx={{
        backgroundColor: 'transparent',
        borderColor: colors.border.subtle,
        width: '100%',
      }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        {editing ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <TextField
              label="Title"
              size="small"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              sx={textFieldSx}
            />
            <TextField
              label="Content"
              size="small"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              multiline
              minRows={2}
              maxRows={8}
              fullWidth
              sx={textFieldSx}
            />
            <TextField
              select
              label="Category"
              size="small"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              fullWidth
              sx={textFieldSx}
            >
              {NOTE_CATEGORIES.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                size="small"
                onClick={() => setEditing(false)}
                disabled={busy}
                sx={{ textTransform: 'none', color: colors.text.secondary }}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleSave}
                disabled={!title.trim() || busy}
                sx={{ textTransform: 'none' }}
              >
                {busy ? <CircularProgress size={18} /> : 'Save'}
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: colors.text.primary,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {note.title || 'Untitled Note'}
                </Typography>
                {note.category && (
                  <Chip
                    label={note.category}
                    size="small"
                    variant="outlined"
                    sx={{
                      mt: 0.5,
                      height: 20,
                      fontSize: '0.65rem',
                      color: colors.text.secondary,
                      borderColor: colors.border.subtle,
                    }}
                  />
                )}
              </Box>
              <Tooltip title="Edit note">
                <IconButton
                  size="small"
                  onClick={startEditing}
                  sx={{ color: colors.icon.secondary }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete note">
                <IconButton
                  size="small"
                  onClick={handleDelete}
                  disabled={busy}
                  sx={{ color: colors.icon.secondary }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            {note.content && (
              <Typography
                variant="body2"
                sx={{
                  mt: 0.75,
                  color: colors.text.secondary,
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {note.content}
              </Typography>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

NoteEditItem.propTypes = {
  note: PropTypes.shape({
    _id: PropTypes.string,
    id: PropTypes.string,
    title: PropTypes.string,
    content: PropTypes.string,
    category: PropTypes.string,
  }).isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default NoteEditItem;
